"use client";
import { RichText as RichTextConverter } from "@payloadcms/richtext-lexical/react";
import { InfoInfoHexaToggleBlockData } from "@/page-builder/fragments/InfoHexaToggleBlock/types";
import { KeyboardEvent, useState, useEffect, useRef } from "react";
import Heading from "@/components/molecules/Heading";
import { SerializedEditorState } from "@payloadcms/richtext-lexical/lexical";
import { convertLexicalWithInternalLinks } from "@/lib/lexical";

export const TitularInfo = ({ title, description, cards = [] }: InfoInfoHexaToggleBlockData) => {
  const [activeCard, setActiveCard] = useState<string | null>(cards[0]?.title || null);
  const [deviceType, setDeviceType] = useState("desktop");
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const handleCardClick = (key: string) => {
    setActiveCard(key);
  };

  const getCardId = (title: string) =>
    title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const getTabId = (title: string) => `titular-info-tab-${getCardId(title)}`;
  const getPanelId = (title: string) => `titular-info-panel-${getCardId(title)}`;

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, cardIndex: number) => {
    const lastIndex = cards.length - 1;
    let nextIndex: number | null = null;

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = cardIndex === lastIndex ? 0 : cardIndex + 1;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = cardIndex === 0 ? lastIndex : cardIndex - 1;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = lastIndex;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextCard = cards[nextIndex];
    setActiveCard(nextCard.title);
    tabRefs.current[nextCard.title]?.focus();
  };

  useEffect(() => {
    const checkScreenSize = () => {
      if (window.innerWidth < 640) {
        setDeviceType("mobile");
      } else if (window.innerWidth < 1024) {
        setDeviceType("tablet");
      } else {
        setDeviceType("desktop");
      }
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return (
    <section className="container mx-auto flex flex-col gap-32 xl:gap-64 pb-64 xl:pb-96">
      <div className="relative grid grid-cols-1 justify-center gap-8 md:gap-32 md:grid-cols-2">
        <Heading title={title} />
        <RichTextConverter
          className="text-m-regular text-neutral-900 whitespace-pre-line flex items-center"
          data={description as any}
        />
      </div>
      {deviceType === "mobile" && (
        <div className="flex flex-col gap-4" role="tablist" aria-orientation="vertical">
          {cards.map((card, index) => (
            <div
              key={card.title}
              className="w-full max-w-full mx-auto relative"
            >
              <button
                type="button"
                role="tab"
                id={getTabId(card.title)}
                aria-selected={activeCard === card.title}
                aria-controls={getPanelId(card.title)}
                tabIndex={activeCard === card.title ? 0 : -1}
                ref={(el) => {
                  tabRefs.current[card.title] = el;
                }}
                className={`w-full h-[280px] text-l-bold bg-no-repeat bg-contain bg-center flex items-center justify-center text-center overflow-hidden p-[44px] cursor-pointer mb-32 border-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600 ${activeCard === card.title
                  ? "bg-green-600 text-white"
                  : "bg-gray-300 text-primary-900"
                  }`}
                style={{
                  backgroundImage: `url('${activeCard === card.title
                    ? "/Rectangle 16.png"
                    : "/Rectangle 16 vazio.png"
                    }')`,
                }}
                onClick={() => handleCardClick(card.title)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
              >
                <span>{card.title}</span>
              </button>

              {activeCard === card.title && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 z-50 w-[30px] h-[30px]"
                  style={{ top: "290px" }}
                >
                  <img
                    src="/Pointer.svg"
                    alt=""
                    aria-hidden="true"
                    className="w-full h-full"
                  />
                </div>
              )}

              {activeCard === card.title && (
                <div
                  id={getPanelId(card.title)}
                  role="tabpanel"
                  aria-labelledby={getTabId(card.title)}
                  className="relative bg-[#F1F6F3] px-16 py-16 box-border rounded-lg overflow-hidden w-full mx-auto mb-32"
                >
                  <div className="relative w-full md:pl-32 z-10">
                    <div
                    className="text-m-regular text-neutral-900 prose"
                    dangerouslySetInnerHTML={{
                      __html: convertLexicalWithInternalLinks( card?.description as any as SerializedEditorState ) ,
                    }}
                  />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {deviceType === "tablet" && (
        <div className="flex flex-col gap-32">
          <div className="relative grid grid-cols-2 gap-32" role="tablist">
            {cards.slice(0, 2).map((card, index) => (
              <button
                type="button"
                role="tab"
                id={getTabId(card.title)}
                aria-selected={activeCard === card.title}
                aria-controls={getPanelId(card.title)}
                tabIndex={activeCard === card.title ? 0 : -1}
                key={card.title}
                ref={(el) => {
                  tabRefs.current[card.title] = el;
                }}
                className={`xl:h-[280px] h-[220px] text-l-bold bg-no-repeat bg-contain flex items-center justify-center text-center overflow-hidden p-[44px] cursor-pointer border-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600 ${activeCard === card.title
                  ? "bg-green-600 text-white"
                  : "bg-gray-300 text-primary-900"
                  }`}
                style={{
                  backgroundImage: `url('${activeCard === card.title
                    ? "/Rectangle 16.png"
                    : "/Rectangle 16 vazio.png"
                    }')`,
                  backgroundSize: "100% 100%",
                }}
                onClick={() =>
                  handleCardClick(card.title)
                }
                onKeyDown={(event) => handleTabKeyDown(event, index)}
              >
                <div className="flex items-center justify-center">
                  <span className="text-center text-l-bold">{card.title}</span>
                </div>
              </button>
            ))}

            {activeCard &&
              cards.slice(0, 2).some((card) => card.title === activeCard) && (
                <div
                  className="absolute w-[30px] h-[30px] transition-all duration-300"
                  style={{
                    left:
                      cards.findIndex((card) => card.title === activeCard) %
                        2 ===
                        0
                        ? "25%"
                        : "75%",
                    bottom: "-40px",
                    transform: "translateX(-50%)",
                    zIndex: 50,
                  }}
                >
                  <img
                    src="/Pointer.svg"
                    alt=""
                    aria-hidden="true"
                    className="w-full h-full"
                  />
                </div>
              )}
          </div>

          {activeCard &&
            cards.slice(0, 2).some((card) => card.title === activeCard) && (
              <div
                id={getPanelId(activeCard)}
                role="tabpanel"
                aria-labelledby={getTabId(activeCard)}
                className="bg-[#F1F6F3] px-24 py-24 rounded-lg mb-4"
              >
                <div
                    className="text-m-regular text-neutral-900 prose"
                    dangerouslySetInnerHTML={{
                      __html: convertLexicalWithInternalLinks(cards.find((card) => card.title === activeCard)?.description as any as SerializedEditorState) ,
                    }}
                  />
              </div>
            )}

          <div className="relative grid grid-cols-2 gap-32" role="tablist">
            {cards.slice(2).map((card, index) => (
              <button
                type="button"
                role="tab"
                id={getTabId(card.title)}
                aria-selected={activeCard === card.title}
                aria-controls={getPanelId(card.title)}
                tabIndex={activeCard === card.title ? 0 : -1}
                key={card.title}
                ref={(el) => {
                  tabRefs.current[card.title] = el;
                }}
                className={`xl:h-[280px] h-[220px]  text-l-bold bg-no-repeat bg-contain flex items-center justify-center text-center overflow-hidden p-[44px] cursor-pointer border-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600 ${activeCard === card.title
                  ? "bg-green-600 text-white"
                  : "bg-gray-300 text-primary-900"
                  }`}
                style={{
                  backgroundImage: `url('${activeCard === card.title
                    ? "/Rectangle 16.png"
                    : "/Rectangle 16 vazio.png"
                    }')`,
                  backgroundSize: "100% 100%",
                }}
                onClick={() =>
                  handleCardClick(card.title)
                }
                onKeyDown={(event) => handleTabKeyDown(event, index + 2)}
              >
                <div className="flex items-center justify-center h-24">
                  <span className="text-center font-medium text-l-bold">
                    {card.title}
                  </span>
                </div>
              </button>
            ))}

            {activeCard &&
              cards.slice(2).some((card) => card.title === activeCard) && (
                <div
                  className="absolute w-[30px] h-[30px] transition-all duration-300"
                  style={{
                    left:
                      cards.findIndex((card) => card.title === activeCard) %
                        2 ===
                        0
                        ? "25%"
                        : "75%",
                    bottom: "-40px",
                    transform: "translateX(-50%)",
                    zIndex: 50,
                  }}
                >
                  <img
                    src="/Pointer.svg"
                    alt=""
                    aria-hidden="true"
                    className="w-full h-full"
                  />
                </div>
              )}
          </div>

          {activeCard &&
            cards.slice(2).some((card) => card.title === activeCard) && (
              <div
                id={getPanelId(activeCard)}
                role="tabpanel"
                aria-labelledby={getTabId(activeCard)}
                className="bg-primary-100 px-24 py-24 rounded-lg "
              >
                <div
                    className="text-m-regular text-neutral-900 prose"
                    dangerouslySetInnerHTML={{
                      __html: convertLexicalWithInternalLinks( cards.find((card) => card.title === activeCard)?.description as any as SerializedEditorState ) ,
                    }}
                  />
              </div>
            )}
        </div>
      )}

      {deviceType === "desktop" && (
        <div className=" flex flex-col gap-32 items-center w-full  mx-auto">
          <div className="flex  gap-32 justify-center w-full" role="tablist">
            {cards.map((card, index) => (
              <div
                key={card.title}
                className="relative flex-1"
              >
                <button
                  type="button"
                  role="tab"
                  id={getTabId(card.title)}
                  aria-selected={activeCard === card.title}
                  aria-controls={getPanelId(card.title)}
                  tabIndex={activeCard === card.title ? 0 : -1}
                  ref={(el) => {
                    tabRefs.current[card.title] = el;
                  }}
                  className={`h-[220px] w-full bg-no-repeat p-[44px] md:p-[30px] bg-cover flex items-center justify-center text-center overflow-hidden cursor-pointer transition-all duration-300 border-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600 ${activeCard === card.title
                    ? "bg-green-600 text-white"
                    : "bg-gray-300 text-primary-900"
                    }`}
                  style={{
                    backgroundImage: `url('${activeCard === card.title
                      ? "/Rectangle 16.png"
                      : "/Rectangle 16 vazio.png"
                      }')`,
                    backgroundSize: "100% 100%",
                  }}
                  onClick={() => handleCardClick(card.title)}
                  onKeyDown={(event) => handleTabKeyDown(event, index)}
                >
                  <span className="text-l-bold font-bold">{card.title}</span>
                </button>

                {activeCard === card.title && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-[230px] z-50 w-[30px] h-[30px]">
                    <img
                      src="/Pointer.svg"
                      alt=""
                      aria-hidden="true"
                      className="w-full h-full"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {activeCard && (
            <div
              id={getPanelId(activeCard)}
              role="tabpanel"
              aria-labelledby={getTabId(activeCard)}
              className="relative bg-primary-100 w-full px-32 py-32 box-border overflow-hidden rounded-lg"
            >
              <div className="relative w-full pl-16 z-10">
                <div
                  className="text-m-regular text-neutral-900 prose prose-ul:mt-0 prose-ul:pb-8 prose-p:my-0 marker:text-neutral-900"
                  dangerouslySetInnerHTML={{
                    __html: convertLexicalWithInternalLinks( cards.find((card) => card.title === activeCard)?.description as any as SerializedEditorState ) ,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
